import axios, { AxiosError, AxiosResponse, CreateAxiosDefaults } from 'axios';
import { TokenService } from './token.service';
import {
	ApiResponse,
	ExtendedAxiosRequestConfig,
	Tokens
} from 'types/api.types';
import i18next from 'i18next';
import { isTokenValid } from 'utils/helpers';
import { LOCALE, REDIRECT_URL_KEY } from 'utils/constants';
import { RoutesPaths } from 'routes/RoutesPathsEnum';

const tokenService = TokenService.getInstance();

const createAxiosInstance = (config: CreateAxiosDefaults) => {
	const instance = axios.create({
		baseURL: process.env.REACT_APP_API_URL,
		timeout: 60000,
		headers: {
			'Content-Type': 'application/json'
		},
		withCredentials: true,
		...config
	});
	return instance;
};

const api = createAxiosInstance({});
const refreshApi = createAxiosInstance({});

// Refresh token state management
let isRefreshing = false;
let failedQueue: Array<{
	resolve: (value?: unknown) => void;
	reject: (reason?: unknown) => void;
}> = [];

const processQueue = (
	error: Error | null,
	token: string | null = null
): void => {
	failedQueue.forEach(promise => {
		if (error) {
			promise.reject(error);
		} else {
			promise.resolve(token);
		}
	});
	failedQueue = [];
};

const refreshToken = async (): Promise<Tokens> => {
	try {
		const refreshToken = tokenService.getRefreshToken();
		if (!refreshToken) {
			throw new Error('No refresh token available');
		}

		const response = await refreshApi.post<ApiResponse<Tokens>>(
			'/auth/refresh',
			{
				refreshToken
			}
		);

		const newTokens = response.data.data;
		tokenService.setTokens(newTokens);
		return newTokens;
	} catch (error) {
		tokenService.clearTokens();
		throw error;
	}
};

const addHeader = (config: ExtendedAxiosRequestConfig, accessToken: string) => {
	if (!('auth' in config)) {
		config.headers['Authorization'] = `Bearer ${accessToken}`;
	}
	if (config.responseType === 'stream') {
		config.responseType = 'stream';
		config.headers['Accept'] =
			'text/event-stream, application/json, text/plain, */*';
	}
};

const saveCurrentUrl = () => {
	if (!window.location.pathname.includes(RoutesPaths.LOGIN)) {
		const currentUrl = window.location.pathname + window.location.search;
		sessionStorage.setItem(REDIRECT_URL_KEY, currentUrl);
	}
};

const redirectToLogin = () => {
	saveCurrentUrl();
	window.location.href = RoutesPaths.LOGIN;
};

// Request interceptor
api.interceptors.request.use(
	async (config: ExtendedAxiosRequestConfig) => {
		// Ensure headers object exists
		config.headers = config.headers || {};

		const accessToken = tokenService.getAccessToken();

		if (accessToken) {
			if (!isTokenValid(accessToken) && config.url !== '/auth/refresh') {
				try {
					const newTokens = await refreshToken();
					addHeader(config, newTokens.token);
				} catch (error) {
					tokenService.clearTokens();
					redirectToLogin();
					return Promise.reject(error);
				}
			} else {
				addHeader(config, accessToken);
			}
		}
		config.headers['Accept-Language'] =
			i18next.language || localStorage.getItem(LOCALE);
		config.headers['Access-Control-Allow-Origin'] =
			process.env.REACT_APP_API_URL;

		return config;
	},
	(error: AxiosError) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
	(response: AxiosResponse) => response,
	async (error: AxiosError) => {
		const originalRequest = error.config as ExtendedAxiosRequestConfig;

		if (!originalRequest) {
			return Promise.reject(error);
		}

		// Handle 401 Unauthorized errors
		if (error.response?.status === 401 && !originalRequest._retry) {
			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				})
					.then(token => {
						originalRequest.headers.Authorization = `Bearer ${token}`;
						return api(originalRequest);
					})
					.catch(err => Promise.reject(err));
			}

			originalRequest._retry = true;
			isRefreshing = true;

			try {
				const newTokens = await refreshToken();
				processQueue(null, newTokens.token);
				originalRequest.headers.Authorization = `Bearer ${newTokens.token}`;
				return api(originalRequest);
			} catch (refreshError) {
				processQueue(refreshError as Error);
				redirectToLogin();
				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		}

		return Promise.reject(error);
	}
);

export const rawApiClient = api;

export const apiClient = {
	get: <T>(url: string, config = {}) =>
		api.get<T, ApiResponse<T>>(url, config).then(response => response.data),

	post: <T>(url: string, data = {}, config = {}) =>
		api
			.post<T, ApiResponse<T>>(url, data, config)
			.then(response => response.data),

	put: <T>(url: string, data = {}, config = {}) =>
		api
			.put<T, ApiResponse<T>>(url, data, config)
			.then(response => response.data),

	delete: <T>(url: string, config = {}) =>
		api
			.delete<T, ApiResponse<T>>(url, config)
			.then(response => response.data),

	patch: <T>(url: string, data = {}, config = {}) =>
		api
			.patch<T, ApiResponse<T>>(url, data, config)
			.then(response => response.data)
};

export default apiClient;
